import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { baseURL } from "config/index";

const AUTH_TIMEOUT_MS = 10000;

// Allowlist of paths the CMS is allowed to land on after the handshake.
// Mirrored on the CMS side in src/config/config.ts (ALLOWED_EXTERNAL_DESTINATIONS).
const ALLOWED_DESTINATIONS = ["/", "/cms/segments", "/analytics"];

const isValidOrigin = (origin, expectedOrigin) => {
  try {
    const originUrl = new URL(origin);
    const expectedUrl = new URL(expectedOrigin);
    return originUrl.origin === expectedUrl.origin;
  } catch {
    return false;
  }
};

const createSecurePayload = (token, source, destination) => ({
  type: "CRM_SECURE_AUTH",
  payload: {
    authToken: token,
    timestamp: Date.now(),
    context: {
      source,
      destination,
      requestId: crypto.randomUUID(),
    },
  },
});

export const openCmsWindow = (destination = "/") => {
  if (!ALLOWED_DESTINATIONS.includes(destination)) {
    toast.error("Invalid destination");
    return;
  }

  const token = Cookies.get("token");
  if (!token) {
    toast.error("Authentication token not found.");
    return;
  }

  const source = "crm";
  let authTimeoutId;
  let cmsWindow;

  try {
    const CMS_URL = baseURL.CMS_URL || "https://erp.cms.traya.health";
    const cmsUrl = new URL(`${CMS_URL}/external/${source}`);
    cmsWindow = window.open(cmsUrl.toString(), "_blank");

    if (!cmsWindow) {
      toast.error(
        "Failed to open CMS window. Please check your pop-up blocker settings."
      );
      return;
    }

    const messageHandler = (event) => {
      if (!isValidOrigin(event.origin, CMS_URL)) {
        console.warn("Invalid origin:", event.origin);
        return;
      }
      if (!event.data?.type) return;

      const { type, payload } = event.data;

      switch (type) {
        case "CMS_READY":
          if (cmsWindow) {
            const securePayload = createSecurePayload(token, source, destination);
            cmsWindow.postMessage(securePayload, CMS_URL);
          }
          break;
        case "CMS_AUTH_RESULT":
          if (payload?.status === "success") {
            cleanup();
            toast.success("Successfully connected to CMS");
          } else if (payload?.status === "error") {
            cleanup();
            toast.error(payload.message || "Authentication failed");
          }
          break;
        default:
          console.warn("Unhandled message type:", type);
      }
    };

    const cleanup = () => {
      clearTimeout(authTimeoutId);
      window.removeEventListener("message", messageHandler);
    };

    authTimeoutId = setTimeout(() => {
      cleanup();
      toast.error("CMS Authentication timed out. Please try again.");
      cmsWindow?.close();
    }, AUTH_TIMEOUT_MS);

    window.addEventListener("message", messageHandler);
  } catch (error) {
    toast.error(error.message || "Failed to open CMS. Please try again later.");
    cmsWindow?.close();
  }
};
